import ModalOverlay from "@components/ModalOverlay";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import { richTextHelper } from "@helpers";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { safeJsonParse } from "@utils/safeJsonParse";

/** @typedef {{ nodeType?: string, data?: Record<string, unknown>, content?: RichTextNode[], value?: string }} RichTextNode */

const Span = styled.span`
  > div {
    width: ${(props) => props.show === true && "80vw"};
    height: ${(props) => props.show === true && "100vh"};
    z-index: 9999;
  }
`;

const ClickableSpan = styled.span`
  text-decoration: underline;
  cursor: pointer;
  font-size: ${(props) => (props.applyLargeFont ? "18px" : "14px")};

  &:hover {
    cursor: pointer;
  }
`;

function TransformStringWithModal({
	original,
	textForLightbox,
	lightboxContent,
	headerFontSize = "small",
}) {
	const [showModal, setShowModal] = useState(false);
	const [isMounted, setIsMounted] = useState(false);
	const lastActiveElement = useRef(null);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	function openModal() {
		lastActiveElement.current = document.activeElement;
		setShowModal(true);
	}

	function closeModal() {
		if (lastActiveElement.current) {
			lastActiveElement.current.focus();
		}
		setShowModal(false);
	}

	const ApplyLargeFont = headerFontSize === "large";

	const options = {
		renderText: (text) => {
			if (textForLightbox && text.includes(textForLightbox)) {
				return text.split(textForLightbox).flatMap((part, i, array) => {
					if (i === array.length - 1) {
						return part;
					}
					return [
						part,
						<ClickableSpan
							applyLargeFont={ApplyLargeFont}
							onClick={openModal}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									openModal();
								}
							}}
							tabIndex={0}
							as="button"
						>
							{textForLightbox}
						</ClickableSpan>,
					];
				});
			}
			return text;
		},
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
		},
	};

	let contentDisplay;
	const parsedOriginal = safeJsonParse(original, null, {
		component: "TransformStringWithModal",
		action: "parse_modal_content",
	});

	if (parsedOriginal.success) {
		/** @type {RichTextNode | null} */
		let richTextDocument = parsedOriginal.value;

		if (richTextDocument?.nodeType === "text") {
			richTextDocument = {
				nodeType: "document",
				data: {},
				content: [
					{
						nodeType: "paragraph",
						data: {},
						content: [richTextDocument],
					},
				],
			};
		}

		if (richTextDocument?.nodeType === "document") {
			contentDisplay = documentToReactComponents(richTextDocument, options);
		}
	}

	if (contentDisplay === undefined) {
		const textToSplit =
			typeof original === "object" && original.value
				? original.value
				: original;

		const splitText = textToSplit.split(textForLightbox);
		contentDisplay = splitText.map((textPart, index) => (
			<React.Fragment key={index + textPart}>
				{textPart}
				{index !== splitText.length - 1 && (
					<ClickableSpan
						applyLargeFont={ApplyLargeFont}
						onClick={openModal}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								openModal();
							}
						}}
						role="button"
						tabIndex={0}
					>
						{textForLightbox}
					</ClickableSpan>
				)}
			</React.Fragment>
		));
	}

	let portalContent = null;

	if (isMounted && lightboxContent) {
		portalContent = ReactDOM.createPortal(
			<ModalOverlay show={showModal} onClose={() => closeModal()}>
				{richTextHelper(lightboxContent)}
			</ModalOverlay>,
			document.getElementById("modal-root"),
		);
	}

	return (
		<Span
			show={showModal}
			style={{
				fontSize: ApplyLargeFont ? "18px" : "14px",
			}}
		>
			{contentDisplay}
			{portalContent}
		</Span>
	);
}

export default TransformStringWithModal;
